import { Injectable } from "@angular/core";
import { Tradable } from "@models/interfaces/tradable";
import { Candle } from "@models";
import { Index } from "@models/asset/index/index.model";
import { AccountService } from "../../../account/services/account.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { Observable, switchMap } from "rxjs";
import { map } from "rxjs/operators";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { FiatService } from "@main-services/currency";
import { parseList, parseObject } from "@helpers/functions";
import { OhlcData } from "@models/charts/ohlc-data";
import { Timespan } from "@helpers/const/timespan";
import { Dictionary } from "@helpers/types";

@Injectable({
	providedIn: "root"
})
export class ChartService {
	public constructor(
		private apicallService: ApicallService,
		private accountService: AccountService,
		private tradableListService: TradableListService,
		private fiatService: FiatService
	) {}

	private getOhlcUrl(assetPid: string, fiatSymbol: string, timespan: Timespan, queryParams: string = ""): string {
		return `ohlc/${encodeURIComponent(assetPid)}/${fiatSymbol}/${timespan}${queryParams}`;
	}

	private fetchAndParseOhlc(url: string): Observable<OhlcData> {
		const apicallOption = new ApicallOption(OhlcData);
		apicallOption.raw = true; // We don't want to only use the `data` property

		return this.apicallService.get(url, apicallOption).pipe(
			map((res) => {
				const candles = parseList(res["data"], Candle);
				return this.parseOhlcData(res, candles);
			})
		);
	}

	public getOhlc(asset: Tradable, timespan: Timespan, startDate?: string, endDate?: string): Observable<OhlcData> {
		const defaultFiat = this.accountService.getDefaultFiat().getSymbol();
		const euroFiat = this.fiatService.getItemById("1").getSymbol();
		const assetPid = asset.getPid();
		const fiatSymbol = asset instanceof Index ? euroFiat : defaultFiat; // Charts for Index should always be in Euro
		const queryParams =
			startDate && endDate
				? "?" +
					new URLSearchParams({
						start_date: startDate,
						end_date: endDate
					}).toString()
				: "";

		const ohlcUrl = this.getOhlcUrl(encodeURIComponent(assetPid), fiatSymbol, timespan, queryParams);
		return this.fetchAndParseOhlc(ohlcUrl);
	}

	public getOhlcByPid(
		assetPid: string,
		timespan: Timespan,
		fiatSymbol: string = this.accountService.getDefaultFiat().getSymbol(),
		startDate?: string,
		endDate?: string
	): Observable<OhlcData> {
		const queryParams =
			startDate && endDate
				? "?" +
					new URLSearchParams({
						start_date: startDate,
						end_date: endDate
					}).toString()
				: "";

		const ohlcUrl = this.getOhlcUrl(encodeURIComponent(assetPid), fiatSymbol, timespan, queryParams);
		return this.fetchAndParseOhlc(ohlcUrl);
	}

	/**
	 * Get the OHLC data for multiple assets
	 * @param assetPids
	 * @param timespan
	 * @param fiatSymbol
	 * @param startDate
	 * @param endDate
	 */
	public getOhlcByAssetPids(
		assetPids: string[],
		timespan: Timespan,
		fiatSymbol: string = this.accountService.getDefaultFiat().getSymbol(),
		startDate?: string,
		endDate?: string
	): Observable<Dictionary<OhlcData>> {
		const queryParams: string =
			startDate && endDate
				? "?" +
					new URLSearchParams({
						start_date: startDate,
						end_date: endDate
					}).toString()
				: "";

		const options: ApicallOption = new ApicallOption(OhlcData);
		options.params = { "assets[]": assetPids };
		options.raw = true; // We don't want to only use the `data` property

		const url = `ohlc/${fiatSymbol}/${timespan}?${queryParams}`;
		return this.apicallService.get(url, options).pipe(
			map((res) => {
				const data = res["data"];
				const response: Dictionary<OhlcData> = {};

				Object.keys(data).map((key) => {
					const candles = data[key].map((candle) => parseObject(candle, Candle));
					response[key] = this.parseOhlcData(data, candles);
				});
				return response;
			})
		);
	}

	/**
	 * This function is a compromise for the fact that we don't have a "multiple ohlc" endpoint that returns the last trading day data.
	 * We use the "single ohlc" endpoint to get the last trading day data for the first asset and then we use the "multiple ohlc" endpoint for the rest.
	 * @param assetPids
	 * @param timespan
	 * @param fiatSymbol
	 */
	public getOhlcMultipleDict$(
		assetPids: string[],
		timespan: Timespan,
		fiatSymbol: string = this.accountService.getDefaultFiat().getSymbol()
	): Observable<Dictionary<OhlcData>> {
		// if we have more than 1 asset, we fetch one with "single ohlc" and the rest with "multiple ohlc" endpoint
		// and ONLY if timespan is DAY (otherwise intraday data is irrelevant)
		if (assetPids.length > 1 && timespan === Timespan.Day) {
			const firstPid = assetPids[0];
			const otherPids = assetPids.slice(1);

			const firstOhlc$ = this.getOhlcByPid(firstPid, timespan, fiatSymbol);
			const otherOhlc$ = this.getOhlcByAssetPids(otherPids, timespan, fiatSymbol);

			return firstOhlc$.pipe(
				switchMap((firstOhlc) => {
					const lastTradingDayStart = firstOhlc.lastTradingDayStart;
					const lastTradingDayEnd = firstOhlc.lastTradingDayEnd;

					return otherOhlc$.pipe(
						// API returns uppercase pids, but we want to use lowercase pids to be consistent with the rest of the app
						map((ohlcDataDict) => this.lowercasePidsInDictionary(ohlcDataDict)),
						map((otherOhlcDict) => {
							const combinedDict = {};

							Object.keys(otherOhlcDict).forEach((key) => {
								const ohlcData = otherOhlcDict[key];
								ohlcData.lastTradingDayStart = lastTradingDayStart;
								ohlcData.lastTradingDayEnd = lastTradingDayEnd;

								combinedDict[key] = ohlcData;
							});

							return { [firstPid]: firstOhlc, ...combinedDict };
						})
					);
				})
			);
		} else if (assetPids.length === 1) {
			return this.getOhlcByPid(assetPids[0], timespan, fiatSymbol).pipe(
				map((ohlcData) => ({ [assetPids[0]]: ohlcData }))
			);
		}

		// in all other cases we use the "multiple ohlc" endpoint (because we don't care about the last trading day data)
		return this.getOhlcByAssetPids(assetPids, timespan, fiatSymbol).pipe(
			// API returns uppercase pids, but we want to use lowercase pids to be consistent with the rest of the app
			map((ohlcDataDict) => this.lowercasePidsInDictionary(ohlcDataDict))
		);
	}

	/**
	 * parses the data + candles into a OhlcData object
	 * @param data
	 * @param candles
	 * @private
	 */
	private parseOhlcData(data, candles: Candle[]): OhlcData {
		if (data["last_trading_day"]) {
			const lastTradingDayStart = data["last_trading_day"]["start_time"];
			const lastTradingDayEnd = data["last_trading_day"]["end_time"];

			return new OhlcData(candles, lastTradingDayStart, lastTradingDayEnd);
		}

		return new OhlcData(candles, "", "");
	}

	private lowercasePidsInDictionary(dict: Dictionary<OhlcData>): Dictionary<OhlcData> {
		const result = {};
		Object.keys(dict).forEach((key) => {
			result[key.toLowerCase()] = dict[key];
		});
		return result;
	}
}
